import { DependencyList, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBowser } from 'hooks/useBowser/useBowser';
import { usePlayStoreUrl } from 'hooks/usePlayStoreUrl';
import { parseUrl, stringifyUrl, parse } from 'query-string';

export const US_APPLE_APP_STORE_URL = 'https://apps.apple.com/us/app/clover/id428620381';
export const US_GOOGLE_PLAY_STORE_URL = 'https://play.google.com/store/apps/details';

const noop = () => undefined;

/**
 * Attempts to redirect the user to the platform and market specific app store
 *
 * @param fallback the callback that runs if there is no redirect
 * @param deps dependencies for the callback
 */
export function useAppStoreRedirectEffect(fallback?: () => void, deps: DependencyList = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fn = useCallback(fallback ?? noop, deps);

  const [, i18n] = useTranslation();
  const { language } = i18n;
  const country = language.split('-')[1] || undefined;

  const bowser = useBowser();
  const { name } = bowser.getOS();
  const { vendor, type } = bowser.getPlatform();

  const { url, query: { referrer, ...query } } = useMemo(() => parseUrl(window.location.href), []);

  const appUrl = useMemo(() => stringifyUrl({ url, query }), [query, url]);

  const playStoreUrl = usePlayStoreUrl(appUrl, () => {
    if (!referrer || !referrer[0]) return {};

    return parse(Array.isArray(referrer) ? referrer[0] : referrer);
  }, [referrer]);

  const replaceUrl = useMemo(() => {
    switch (country?.toLowerCase()) {
      // US ONLY
      case 'us':
        switch (name?.toLowerCase()) {
          case 'ios':
            return US_APPLE_APP_STORE_URL;
          case 'android':
            // not on amazon app store
            if (vendor?.toLowerCase() === 'amazon') return;

            return playStoreUrl;
        }
        break;
    }
  }, [country, name, playStoreUrl, vendor]);

  useEffect(() => {
    if (replaceUrl) window.location.replace(replaceUrl);

    // delay so replace can run
    const id = setTimeout(() => {
      // only close if not desktop
      if (type?.toLowerCase() !== 'desktop') {
        window.close();

        return;
      }

      fn();
    }, 240);

    return () => clearTimeout(id);
  }, [fn, replaceUrl, type]);

}
